import { useAuth0 } from '@auth0/auth0-react';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { StringParam, useQueryParams } from 'use-query-params';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { AppDispatch } from '../../../app/store';
import {
  ChartDropdownDimensionEnum,
  CubeMeasureEnum,
  FeatureEnum,
  selectCurrChartDimension,
  setCurrChartDimension,
} from '../../../services/data/impl/cube/cubeSlice';
import {
  selectCurrBlockID,
  selectCurrCustomerID,
  selectCurrFeature,
  selectCurrMeasure,
  selectCurrPointId,
  selectCurrRanchID,
  selectCurrScanID,
  selectCurrYear,
  setCurrBlockByID,
  setCurrCustomerByID,
  setCurrFeature,
  setCurrMeasure,
  setCurrPointId,
  setCurrRanchByID,
  setCurrScanByID,
  setCurrYear,
} from '../../select/selectSlice';
import {
  RanchPageViewEnum,
  selectRanchPageView,
  setRanchPageView,
} from '../ranchesSlice';

type StringParamValue = string | null | undefined;

const MapQueryParamToStateActions = {
  customerID: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrCustomerByID(value || ''));
  },
  ranchID: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrRanchByID(value || ''));
  },
  year: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrYear(value || ''));
  },
  blockID: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrBlockByID(value || ''));
  },
  scanID: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrScanByID(value || ''));
  },
  pointID: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrPointId(value || ''));
  },
  feature: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(setCurrFeature((value || FeatureEnum.none) as FeatureEnum));
  },
  chartDimension: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(
      setCurrChartDimension(
        (value ||
          ChartDropdownDimensionEnum.distribution) as ChartDropdownDimensionEnum
      )
    );
  },
  ranchPageView: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(
      setRanchPageView(
        (value || RanchPageViewEnum.ranchSummary) as RanchPageViewEnum
      )
    );
  },
  measure: (value: StringParamValue, dispatch: AppDispatch) => {
    dispatch(
      setCurrMeasure(
        (value ||
          CubeMeasureEnum.detectionFactAvgCountPerMeter) as CubeMeasureEnum
      )
    );
  },
};

/**
 * Hook to manage the query parameters for the ranch summary page
 *
 * @deprecated This is actually just the useQueryParamsManager hook, but renamed and now used only at a page level.
 * This is still considered legacy logic, do not replicate what you see here.
 */
export const useRanchSummaryPageParams = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { isAuthenticated } = useAuth0();

  const currCustomerID = useAppSelector(selectCurrCustomerID);
  const currRanchID = useAppSelector(selectCurrRanchID);
  const currYear = useAppSelector(selectCurrYear);
  const currBlockID = useAppSelector(selectCurrBlockID);
  const currScanID = useAppSelector(selectCurrScanID);
  const currPointID = useAppSelector(selectCurrPointId);
  const currFeature = useAppSelector(selectCurrFeature);
  const currChartDimension = useAppSelector(selectCurrChartDimension);
  const ranchPageView = useAppSelector(selectRanchPageView);
  const currMeasure = useAppSelector(selectCurrMeasure);

  const [query, setQuery] = useQueryParams({
    customerID: StringParam,
    ranchID: StringParam,
    year: StringParam,
    blockID: StringParam,
    scanID: StringParam,
    pointID: StringParam,
    feature: StringParam,
    chartDimension: StringParam,
    ranchPageView: StringParam,
    measure: StringParam,
  });

  // Restore state from url params
  useEffect(() => {
    Object.entries(query).map(([param, value]) => {
      MapQueryParamToStateActions[
        param as keyof typeof MapQueryParamToStateActions
      ](value, dispatch);
    });
  }, []);

  // observe state and update url params
  useEffect(() => {
    if (isAuthenticated) {
      setQuery({
        customerID: currCustomerID || null,
        ranchID: currRanchID || null,
        year: currYear || null,
        blockID: currBlockID || null,
        scanID: currScanID || null,
        pointID: currPointID || null,
        feature: currFeature,
        chartDimension: currChartDimension,
        ranchPageView: ranchPageView,
        measure: currMeasure,
      });
    }
  }, [
    location.search, // Only look at search field since location is an object and will always be a new reference with different key even if the search field is the same.  This change was necessary to fix a bug in firefox where the dashboard crashed on initial load: "Too many calls to Location or History APIs within a short timeframe." and "DOMException: The operation is insecure."
    currCustomerID,
    currRanchID,
    currYear,
    currBlockID,
    currScanID,
    currPointID,
    currFeature,
    currChartDimension,
    ranchPageView,
    isAuthenticated,
  ]);
};
